import itemService from '../../services/items/ItemService'

export const itemModule = {
  namespaced: true,
  state: {
    products: [],
    pagination: {
      total: 0,
      current_page: 1,
      lastPage: 1,
      page: 1,
      loading: false,
      loadingMore: false,
      allLoaded: false,
    },
    filtros: {
      fabricante: [],
      grupo: [],
      marca: [],
      listaOferta: null,
      foraLinha: false,
      saldoMin: 0,
      tipoPreco: 0,
      moeda: 0,
      percentualDesconto: 0,
      // Termo de busca livre (descrição, código ou código de barras). Mora
      // aqui, e não no componente do campo, para que
      // `populateFiltersPayload()` o emita sozinho: assim a grade, o PDF e o
      // link compartilhado enxergam o mesmo recorte. Com "Selecionar Todos"
      // ativo o back monta o resultado pelos filtros — sem `search` o PDF
      // sairia com o catálogo inteiro em vez do que está na tela.
      search: '',
    },
    // código → "Nome (código)" dos grupos, vindo de `getFiltros` (#51). O
    // filtro trafega o código; o painel de filtros ativos usa este mapa para
    // mostrar o nome.
    grupoRotulos: {},
    selectedItems: [],
    selectAllItems: false,
    // Itens desmarcados manualmente enquanto "Selecionar Todos" está ativo.
    // Vira o parâmetro `itensExcluidos` enviado ao back (PDF / link público).
    deselectedItems: [],
    // Última mensagem de erro da listagem (ex.: 403 de permissão revogada).
    // A view observa este estado para exibir o feedback ao usuário.
    error: null,
  },
  mutations: {
    SET_ERROR(state, payload) {
      state.error = payload
    },

    SET_PRODUCTS(state, { data, reset }) {
      if (reset) {
        state.products = Object.freeze(data)
      } else {
        state.products = Object.freeze([...state.products, ...data])
      }
    },

    SET_FILTERS(state, payload) {
      state.filtros = { ...payload }
    },

    SET_GRUPO_ROTULOS(state, grupos) {
      const rotulos = {}
      ;(grupos || []).forEach((grupo) => {
        if (grupo && typeof grupo === 'object') rotulos[grupo.value] = grupo.text
      })
      state.grupoRotulos = Object.freeze(rotulos)
    },

    SET_PAGINATION(state, payload) {
      state.pagination = { ...state.pagination, ...payload }
    },

    SET_SELECTED_ITEMS(state, payload) {
      state.selectedItems = payload
    },

    SET_SELECTED_ALL_ITEMS(state, payload) {
      state.selectAllItems = payload
    },

    SET_DESELECTED_ITEMS(state, payload) {
      state.deselectedItems = payload
    },

    RESET_PRODUCTS(state) {
      state.products = []
      state.pagination = {
        total: 0,
        current_page: 1,
        lastPage: 1,
        page: 1,
        loading: false,
        loadingMore: false,
        allLoaded: false,
      }
      // Não zera mais a seleção (#70): ela sobrevive à troca de filtro, e
      // quem decide quando ela acaba é o CatalogoApp. O state da seleção
      // continua aqui — é dele que o PDF, o link e o lote de códigos leem.
    },
  },
  actions: {
    setFilters({ commit }, payload) {
      commit('SET_FILTERS', payload)
    },

    setSelectAllItems({ commit }, payload) {
      commit('SET_SELECTED_ALL_ITEMS', payload)
    },

    async fetchProducts({ commit, state }, { reset, filters }) {
      commit('SET_ERROR', null)
      if (reset) {
        commit('RESET_PRODUCTS')
        commit('SET_PAGINATION', { loading: true })
      }
      try {
        const params = { ...filters, page: state.pagination.page }
        const result = await itemService.getItens(params)
        commit('SET_PRODUCTS', { data: result.data, reset })
        commit('SET_PAGINATION', {
          total: result.total,
          lastPage: result.last_page,
          current_page: result.current_page,
          page: result.current_page + 1,
          allLoaded: result.current_page >= result.last_page,
        })
      } catch (err) {
        console.error('Erro ao carregar produtos:', err)
        // Permissão revogada volta como 403 com mensagem já traduzida pelo
        // backend; sem isso o usuário só via a grade esvaziar sem explicação.
        commit('SET_ERROR', err?.message ?? null)
        if (reset) {
          commit('RESET_PRODUCTS')
        }
      } finally {
        commit('SET_PAGINATION', { loading: false, loadingMore: false })
      }
    },
    async loadMoreProducts({ commit, state, dispatch }, filters) {
      if (
        state.pagination.loadingMore ||
        state.pagination.loading ||
        state.pagination.allLoaded ||
        state.products.length === 0
      ) {
        return
      }
      commit('SET_PAGINATION', { loadingMore: true })
      await dispatch('fetchProducts', { reset: false, filters })
    },
    async generatePdf({ commit }, filters) {
      try {
        const params = { ...filters }
        delete params.page
        delete params.per_page
        const response = await itemService.generatePdf(params)
        return response
      } catch (err) {
        console.error('Erro ao gerar PDF:', err)
        throw err
      }
    },
    // Lote de códigos de barras (#66): uma parte por chamada (`page` a partir
    // de 1). A lista não vai para o state, só alimenta o ZIP que o diálogo
    // monta e descarta.
    async fetchBarcodes({}, { filters, page }) {
      const params = { ...filters, page }
      delete params.per_page
      const result = await itemService.getBarcodes(params)
      return {
        items: result?.items ?? [],
        hasMore: result?.hasMore === true,
      }
    },
    // PDF de etiquetas (#67): uma parte por chamada, como o ZIP. `labels` é o
    // layout já normalizado (normalizeLabelOptions). `bars` abre a lista de
    // blocos (#72): só as barras ainda é uma lista não vazia — vazia, o
    // Laravel a leria como ausente, e ausente é a etiqueta de antes.
    async fetchBarcodeLabelsPdf({}, { filters, page, labels }) {
      const params = {
        ...filters,
        page,
        label_layout: labels.layout,
        label_fields: ['bars', ...labels.fields].join(','),
      }
      if (labels.layout === 'grid') {
        params.label_cols = labels.cols
        params.label_rows = labels.rows
      }
      delete params.per_page
      const response = await itemService.getBarcodeLabelsPdf(params)
      const headers = response.headers || {}
      return {
        blob: new Blob([response.data], { type: 'application/pdf' }),
        hasMore: headers['x-labels-has-more'] === '1',
        count: parseInt(headers['x-labels-count'], 10) || 0,
        skipped: parseInt(headers['x-labels-skipped'], 10) || 0,
      }
    },
    async getFiltros({ commit }) {
      const response = await itemService.getFiltros()
      commit('SET_GRUPO_ROTULOS', response?.grupos)
      return response
    },
    // nao guardamos as imagens em state: sao data URIs base64 de varios MB por
    // item, o cache de "mesmo item reaberto" fica no componente
    async fetchItemImages({}, codigo) {
      const result = await itemService.getItemImages(codigo)
      return result?.images ?? []
    },
    setSelectedItems({ commit }, payload) {
      commit('SET_SELECTED_ITEMS', payload)
    },

    setDeselectedItems({ commit }, payload) {
      commit('SET_DESELECTED_ITEMS', payload)
    },
  },
  getters: {
    filtros: (state) => state.filtros,
    products: (state) => state.products,
    pagination: (state) => state.pagination,
    loading: (state) => state.pagination.loading,
    loadingMore: (state) => state.pagination.loadingMore,
    selectedItems: (state) => state.selectedItems,
    selectAllItems: (state) => state.selectAllItems,
    deselectedItems: (state) => state.deselectedItems,
    // Quantos itens a próxima geração levaria. Não é `selectedItems.length`:
    // com "Selecionar Todos" ativo a seleção é o filtro inteiro (inclusive as
    // páginas ainda não carregadas) menos o que foi desmarcado à mão — a mesma
    // conta que o back faz com `itensExcluidos`.
    selectedCount: (state) =>
      state.selectAllItems
        ? Math.max(state.pagination.total - state.deselectedItems.length, 0)
        : state.selectedItems.length,
    error: (state) => state.error,
    grupoRotulos: (state) => state.grupoRotulos,
  },
}
